import api, { publicApi } from './axios';

// Tenant Configuration (PUBLIC - no auth required)
export const getTenantConfig = (domain) =>
  publicApi.get('/tenant/config', { params: { domain } });

// Authentication (use publicApi — no auth token needed for login/register)
export const register = (data) => publicApi.post('/auth/register', data);
export const login = (data) => publicApi.post('/auth/login', data);
export const getCurrentUser = () => api.get('/auth/me');
export const updateUserProfile = (data) => api.patch('/auth/profile', data);

// Questions
export const getQuestions = (params) => api.get('/questions', { params });
export const getSmartQuestions = (params) => api.get('/questions/smart', { params });
export const getAdaptiveQuestions = (params) => api.get('/questions/adaptive', { params });
export const submitAnswer = (data) => api.post('/questions/answer', data);
export const importQuestions = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/questions/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const reportQuestion = (data) => api.post('/questions/report', data);
export const finishSession = () => api.post('/session/finish');

// AI Generation
export const generateAIQuestions = (data) => api.post('/ai/generate', data);
export const getAIUsage = () => api.get('/ai/usage');

// AI Scaffolding (Elite)
export const getScaffoldingStatus = () => api.get('/scaffolding/status');

// Analytics
export const getAnalytics = () => api.get('/analytics');
export const getStudySessions = (params) => api.get('/analytics/sessions', { params });
export const getCalibrationStats = () => api.get('/analytics/calibration');
export const getSpacedRepetitionStats = () => api.get('/analytics/spaced-repetition');
export const getWrongAnswers = () => api.get('/analytics/wrong-answers');

// Progression Tracking
export const getUserProgression = (params) => api.get('/user/progression', { params });
export const getAdminUserProgression = (userId, params) => api.get(`/admin/user/${userId}/progression`, { params });

// Exam Mode
export const startExam = (data) => api.post('/exam/start', null, { params: data });
export const getExamQuestions = (examId) => api.get(`/exam/${examId}/questions`);
export const submitExamAnswer = (examId, data) => api.post(`/exam/${examId}/answer`, null, { params: data });
export const completeExam = (examId) => api.post(`/exam/${examId}/complete`);

// Data Export
export const exportData = (data) =>
  api.post('/export/data', data, { responseType: 'blob' });

// Sample Data
export const initSampleData = () => api.post('/init/sample-data');

// UNE Priority System
export const getUnlockStatus = () => api.get('/unlock-status');
export const getQuestionBankStats = () => api.get('/question-bank-stats');
export const getHomeTopicProgress = () => api.get('/home/topic-progress');
export const getHomePrePostComparison = () => api.get('/home/pre-post-comparison');
export const initUNEQuestions = () => api.post('/init/une-questions');

// Admin Tenant Management
export const getAdminTenants = () => api.get('/admin/tenants');
export const createAdminTenant = (data) => api.post('/admin/tenants', data);
export const updateAdminTenant = (tenantId, data) => api.put(`/admin/tenants/${tenantId}`, data);
export const deleteAdminTenant = (tenantId) => api.delete(`/admin/tenants/${tenantId}`);
export const getMultiTenantDashboard = () => api.get('/admin/dashboard/multi-tenant');
export const runMigration = () => api.post('/admin/run-migration');

// Job Boards
export const getJobBoards = () => api.get('/jobs/boards');
export const trackJobBoardClick = (boardId) => api.post(`/jobs/boards/${boardId}/click`);

// Admin Job Boards
export const getAdminJobBoards = (params) => api.get('/admin/jobs/boards', { params });
export const createJobBoard = (data) => api.post('/admin/jobs/boards', data);
export const updateJobBoard = (boardId, data) => api.put(`/admin/jobs/boards/${boardId}`, data);
export const deleteJobBoard = (boardId) => api.delete(`/admin/jobs/boards/${boardId}`);
export const getJobBoardAnalytics = (params) => api.get('/admin/jobs/analytics', { params });
export const seedAustralianJobBoards = () => api.post('/admin/jobs/seed-australian');

// Tutoring — Tutor Profile
export const registerAsTutor = (data) => api.post('/tutoring/register', data);
export const getTutorProfile = () => api.get('/tutoring/profile');
export const updateTutorProfile = (data) => api.put('/tutoring/profile', data);
export const getOnboardingLink = () => api.get('/tutoring/onboarding-link');
export const getStripeStatus = () => api.get('/tutoring/stripe-status');
export const getStripeKey = () => api.get('/tutoring/stripe-key');
export const uploadTutorCredential = (credentialType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tutoring/credentials/upload?credential_type=${credentialType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Tutoring — Browse Tutors
export const browseTutors = (params) => api.get('/tutoring/tutors', { params });
export const getTutorDetail = (tutorId) => api.get(`/tutoring/tutors/${tutorId}`);

// Tutoring — Sessions
export const bookSession = (data) => api.post('/tutoring/sessions', data);
export const listSessions = (params) => api.get('/tutoring/sessions', { params });
export const getSessionDetail = (sessionId) => api.get(`/tutoring/sessions/${sessionId}`);
export const joinSession = (sessionId) => api.post(`/tutoring/sessions/${sessionId}/join`);
export const sessionCheckpoint = (sessionId, data) => api.post(`/tutoring/sessions/${sessionId}/checkpoint`, null, { params: data });
export const confirmSession = (sessionId) => api.post(`/tutoring/sessions/${sessionId}/confirm`);
export const cancelSession = (sessionId) => api.post(`/tutoring/sessions/${sessionId}/cancel`);
export const reportNoShow = (sessionId) => api.post(`/tutoring/sessions/${sessionId}/no-show`);
export const leaveReview = (sessionId, data) => api.post(`/tutoring/sessions/${sessionId}/review`, data);
export const reportSessionParticipant = (sessionId, data) => api.post(`/tutoring/sessions/${sessionId}/report`, data);

// Admin Tutoring
export const adminListSessions = (params) => api.get('/admin/tutoring/sessions', { params });
export const adminFlaggedSessions = () => api.get('/admin/tutoring/flagged');
export const adminApprovePayout = (sessionId) => api.post(`/admin/tutoring/sessions/${sessionId}/approve`);
export const adminRejectSession = (sessionId) => api.post(`/admin/tutoring/sessions/${sessionId}/reject`);
export const adminTutoringDashboard = () => api.get('/admin/tutoring/dashboard');
export const adminListReports = (params) => api.get('/admin/tutoring/reports', { params });

// Homework — Tutor
export const createHomework = (data) => api.post('/homework/create', data);
export const aiGenerateHomework = (data) => api.post('/homework/ai-generate', data);
export const importHomework = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/homework/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const listHomeworkAssignments = (params) => api.get('/homework/assignments', { params });
export const getHomeworkAssignment = (id) => api.get(`/homework/assignments/${id}`);
export const updateHomeworkAssignment = (id, data) => api.put(`/homework/assignments/${id}`, data);
export const publishHomework = (id) => api.post(`/homework/assignments/${id}/publish`);
export const submitHomeworkFollowup = (id, data) => api.post(`/homework/assignments/${id}/followup`, data);
export const getPendingFollowups = () => api.get('/homework/followup-pending');

// Homework — Public Quiz (no auth)
export const getQuiz = (accessCode) => api.get(`/quiz/${accessCode}`);
export const submitQuiz = (accessCode, data) => api.post(`/quiz/${accessCode}/submit`, data);
export const getQuizResults = (accessCode, attemptId) => api.get(`/quiz/${accessCode}/results/${attemptId}`);

// A/B Testing / Experiments — Admin
export const getAdminExperiments = (params) => api.get('/admin/experiments', { params });
export const createExperiment = (data) => api.post('/admin/experiments', data);
export const updateExperimentStatus = (experimentId, data) => api.put(`/admin/experiments/${experimentId}/status`, data);
export const getExperimentResults = (experimentId) => api.get(`/admin/experiments/${experimentId}/results`);
export const assignExistingUsersToExperiment = (experimentId) => api.post(`/admin/experiments/${experimentId}/assign-existing-users`);

// A/B Testing / Experiments — User (metrics tracking)
export const recordExperimentMetrics = (sessionDate, data) => api.post(`/sessions/${sessionDate}/experiment-metrics`, data);

// Diagnostic Tests
export const getDiagnosticProgress = () => api.get('/diagnostic/progress');
export const getDiagnosticTest = (testNumber) => api.get(`/diagnostic/${testNumber}`);
export const submitDiagnosticAnswer = (data) => api.post('/diagnostic/answer', data);
export const completeDiagnosticTest = (testNumber) => api.post(`/diagnostic/${testNumber}/complete`);
export const saveDiagnosticQuestionnaire = (data) => api.post('/diagnostic/questionnaire', data);
export const getDiagnosticResults = () => api.get('/diagnostic/results');
export const getDiagnosticFullReport = () => api.get('/diagnostic/full-report');

// Predictions
export const refreshPredictions = () => api.get('/predictions/refresh');

// Exam Dates (Essential+)
export const getExamDates = () => api.get('/exam-dates');
export const createExamDate = (data) => api.post('/exam-dates', data);
export const updateExamDate = (id, data) => api.put(`/exam-dates/${id}`, data);
export const deleteExamDate = (id) => api.delete(`/exam-dates/${id}`);

// Past Test Results (Essential+)
export const getPastResults = () => api.get('/past-results');
export const createPastResult = (data) => api.post('/past-results', data);
export const uploadPastResults = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/past-results/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deletePastResult = (id) => api.delete(`/past-results/${id}`);

// AI Study Plan (All tiers)
export const generateStudyPlan = () => api.post('/study-plan/generate');
export const getStudyPlan = () => api.get('/study-plan');

// Adaptive Engine v2 — Improvement Tracking
export const getImprovementData = () => api.get('/improvement-data');
export const getUneMastery = () => api.get('/une-mastery');
export const getSessionAnalysis = () => api.get('/session-analysis/latest');

// Diagnostic Reports — Admin
export const getDiagnosticReportUsers = () => api.get('/admin/diagnostic-report-users');
export const generateDiagnosticReport = (userId) => api.post(`/admin/diagnostic-report/${userId}`);
export const getDiagnosticReport = (userId) => api.get(`/admin/diagnostic-report/${userId}`);
export const generateAdaptiveProgressReport = (userId) => api.post(`/admin/adaptive-progress-report/${userId}`);
export const getAdaptiveProgressReport = (userId) => api.get(`/admin/adaptive-progress-report/${userId}`);
export const getOverallReport = (userId) => api.get(`/admin/overall-report/${userId}`);

// Admin User Management
export const deactivateUser = (userId) => api.post(`/admin/user/${userId}/deactivate`);
export const reactivateUser = (userId) => api.post(`/admin/user/${userId}/reactivate`);
export const mergeUsers = (data) => api.post('/admin/user/merge', data);

// Affiliate Link Tracking
export const trackAffiliateClick = (data) => api.post('/affiliate/track-click', data);
export const getAffiliateClickStats = () => api.get('/admin/affiliate/click-stats');

// MCQ Database Sync — Admin
export const getAdminSyncStatus = () => api.get('/admin/sync/status');
export const triggerAdminSync = () => api.post('/admin/sync/trigger');
export const getAdminSyncJobStatus = (jobId) => api.get(`/admin/sync/trigger/${jobId}`);
export const getAdminSyncConfig = () => api.get('/admin/sync/config');
export const saveAdminSyncConfig = (data) => api.post('/admin/sync/config', data);
export const testSyncConnection = () => api.post('/admin/sync/test-connection');

// MCQ Local Re-seed (from file on server, no GitHub needed)
export const triggerLocalReseed = () => api.post('/init/mcq-database');
export const getReseedJobStatus = (jobId) => api.get(`/init/mcq-database/status/${jobId}`);
export const checkMcqDatabaseFile = () => api.get('/init/mcq-database/check');

// Admin Question Text Fixes
export const previewQuestionFixes = () => api.get('/admin/questions/preview-fixes');
export const applyQuestionFixes = () => api.post('/admin/questions/apply-fixes');

// Admin Custom Find & Replace
export const previewFindReplace = (data) => api.post('/admin/questions/find-replace-preview', data);
export const applyFindReplace = (data) => api.post('/admin/questions/find-replace-apply', data);

// Admin Text Quality Fixes
export const previewQualityFixes = () => api.post('/admin/questions/quality-fixes-preview');
export const applyQualityFixes = () => api.post('/admin/questions/quality-fixes-apply');

// Admin Encoding & Character Fixes
export const previewEncodingFixes = () => api.post('/admin/questions/encoding-fixes-preview');
export const applyEncodingFixes = () => api.post('/admin/questions/encoding-fixes-apply');

// Admin Word Frequency Scanner
export const getWordFrequency = (params) => api.get('/admin/questions/word-frequency', { params });
export const startSpellingScan = (params) => api.post('/admin/questions/spelling-scan', null, { params });
export const getSpellingScanStatus = (jobId) => api.get(`/admin/questions/spelling-scan/${jobId}`);

// Admin Question Export & Import
export const exportQuestions = () => api.get('/admin/questions/export', { responseType: 'blob' });
export const importQuestionsFile = (fileData) => api.post('/admin/questions/import-file', fileData, {
  headers: { 'Content-Type': 'application/octet-stream' },
  timeout: 600000,  // 10 min timeout for large files
});

// Admin Question Management (Search / Edit / Delete)
export const getDistinctQuestionValues = () => api.get('/admin/questions/distinct-values');
export const searchAdminQuestions = (params) => api.get('/admin/questions/search', { params });
export const getAdminQuestion = (id) => api.get(`/admin/questions/${id}`);
export const updateAdminQuestion = (id, data) => api.put(`/admin/questions/${id}`, data);
export const deleteAdminQuestion = (id) => api.delete(`/admin/questions/${id}`);

// Admin Mobile Flag & Scenario Detection
export const computeMobileFlag = () => api.post('/admin/questions/compute-mobile-flag');
export const detectScenarios = () => api.post('/admin/questions/detect-scenarios');
export const bulkSetScenario = (data) => api.post('/admin/questions/bulk-set-scenario', data);
export const getScenarioStats = () => api.get('/admin/questions/scenario-stats');
export const bulkDeleteQuestions = (ids) => api.post('/admin/questions/bulk-delete', { ids });

// Wrong Answer Review Practice
export const getWrongReviewQuestions = (params) => api.get('/questions/wrong-review', { params });

// Onboarding
export const completeOnboarding = () => api.post('/auth/complete-onboarding');
